import { useCallback, useEffect, useRef, useState } from 'react'
import { api, ApiError } from './api'
import { cache, outbox } from './outbox'

/**
 * Fetch-on-mount with a cache fallback. The field app must keep working when the
 * request fails, so anything given a `cacheKey` is served from IndexedDB when the
 * network is gone, and the caller is told the data is stale.
 */
export function useApi<T>(
  path: string | null,
  opts: { cacheKey?: string; deps?: unknown[] } = {},
) {
  const { cacheKey } = opts
  const [data, setData] = useState<T | null>(null)
  const [error, setError] = useState<Error | null>(null)
  const [loading, setLoading] = useState(!!path)
  const [stale, setStale] = useState(false)
  const [tick, setTick] = useState(0)
  const alive = useRef(true)

  useEffect(() => {
    alive.current = true
    return () => {
      alive.current = false
    }
  }, [])

  useEffect(() => {
    if (!path) {
      setLoading(false)
      return
    }
    let cancelled = false
    setLoading(true)
    setError(null)

    api
      .get<T>(path)
      .then((res) => {
        if (cancelled || !alive.current) return
        setData(res)
        setStale(false)
        setError(null)
        if (cacheKey) void cache.set(cacheKey, res)
      })
      .catch(async (e) => {
        if (cancelled || !alive.current) return
        // Only fall back to the cache when the network actually failed. A 404 or a
        // 403 is the server answering, and showing yesterday's data under "could not
        // be reached" turns a definite answer into a misleading one.
        const networkFailed = !(e instanceof ApiError) || e.status >= 500
        if (cacheKey && networkFailed) {
          const cached = await cache.get<T>(cacheKey)
          if (cached && alive.current && !cancelled) {
            setData(cached)
            setStale(true)
            setError(null)
            setLoading(false)
            return
          }
        }
        setError(e instanceof Error ? e : new Error(String(e)))
      })
      .finally(() => {
        if (!cancelled && alive.current) setLoading(false)
      })

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, cacheKey, tick, ...(opts.deps ?? [])])

  const reload = useCallback(() => setTick((t) => t + 1), [])
  return { data, error, loading, stale, reload, setData }
}

/** A mutation with pending/error state, for buttons that call the API. */
export function useAction<TArgs extends unknown[], TResult>(
  fn: (...args: TArgs) => Promise<TResult>,
) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<ApiError | null>(null)

  const run = useCallback(
    async (...args: TArgs): Promise<TResult | undefined> => {
      setBusy(true)
      setError(null)
      try {
        return await fn(...args)
      } catch (e) {
        setError(e instanceof ApiError ? e : new ApiError(0, 'UNKNOWN', String(e)))
        return undefined
      } finally {
        setBusy(false)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  return { run, busy, error, clearError: () => setError(null) }
}

/** Live online/offline state, plus how long we have been offline. */
export function useConnection() {
  const [online, setOnline] = useState(navigator.onLine)
  const [since, setSince] = useState<number | null>(navigator.onLine ? null : Date.now())

  useEffect(() => {
    const up = () => {
      setOnline(true)
      setSince(null)
    }
    const down = () => {
      setOnline(false)
      setSince(Date.now())
    }
    window.addEventListener('online', up)
    window.addEventListener('offline', down)
    return () => {
      window.removeEventListener('online', up)
      window.removeEventListener('offline', down)
    }
  }, [])

  const offlineMinutes = since ? Math.floor((Date.now() - since) / 60_000) : 0
  return { online, offlineMinutes }
}

/** The number of items waiting in the outbox, live. */
export function useOutboxCount() {
  const [count, setCount] = useState(0)

  useEffect(() => {
    const refresh = () => void outbox.count().then(setCount)
    refresh()
    return outbox.subscribe(refresh)
  }, [])

  return count
}

export function useOutboxItems() {
  const [items, setItems] = useState<Awaited<ReturnType<typeof outbox.all>>>([])

  useEffect(() => {
    let cancelled = false
    const refresh = () => {
      void outbox.all().then((rows) => {
        if (!cancelled) setItems(rows)
      })
    }
    refresh()
    const unsub = outbox.subscribe(refresh)
    return () => {
      cancelled = true
      unsub()
    }
  }, [])

  return items
}

/** A ticking clock for the field status bar. */
export function useClock() {
  const [now, setNow] = useState(() => new Date())
  useEffect(() => {
    const t = window.setInterval(() => setNow(new Date()), 20_000)
    return () => window.clearInterval(t)
  }, [])
  return now
}

/** Best-effort GPS. Never blocks a capture — a missing fix is recorded as missing. */
export function useGeolocation() {
  const [coords, setCoords] = useState<{ lat: number; lng: number; accuracy: number } | null>(null)
  const [denied, setDenied] = useState(false)

  useEffect(() => {
    if (!navigator.geolocation) return
    const id = navigator.geolocation.watchPosition(
      (p) => setCoords({ lat: p.coords.latitude, lng: p.coords.longitude, accuracy: p.coords.accuracy }),
      () => setDenied(true),
      { enableHighAccuracy: true, maximumAge: 30_000, timeout: 15_000 },
    )
    return () => navigator.geolocation.clearWatch(id)
  }, [])

  return { coords, denied }
}
